import type {
  CreatorDeliveryTimeline,
  CreatorTimelineContentNode,
  CreatorDeliveryAction,
  CreatorDeliveryTarget,
  CreatorDeliveryNodeState,
  CreatorDeliveryNode,
  CreatorDeliveryContentCode
} from '@/interface/creatorDeliveryTimeline';
import type { CreatorDeliveryRound } from '@/interface/creatorOrderV2';
import type { CreatorCollaborationPackage } from './model';
import type { CreatorPackageDeliveryMode } from './packageDelivery';
import { CreatorOrderV2ContractError } from '@/apis/creatorOrderV2/errors';

export const getCreatorTimelineContentNodes = (
  timeline?: CreatorDeliveryTimeline | null
) =>
  timeline?.milestones.flatMap((phase) =>
    phase.nodes.filter(
      (node): node is CreatorTimelineContentNode => node.type === 'CONTENT'
    )
  ) ?? [];

/** Account details can be resubmitted after brand feedback, but not edited in place. */
export const canEditCreatorTimelineNode = (node: CreatorTimelineContentNode) =>
  node.code !== 'ACCOUNT_INFO' &&
  node.actions.includes('EDIT_CONTENT') &&
  !!node.data.target;

/** Latest actual round per submission step in API order; pending tasks are not history. */
export const getLatestCreatorTimelineContentNodeIds = (
  nodes: CreatorDeliveryNode[]
) => {
  const latest = new Map<CreatorDeliveryContentCode, string>();
  nodes.forEach((node) => {
    if (node.type === 'CONTENT' && node.data.contentRoundId !== null)
      latest.set(node.code, node.id);
  });
  return new Set(latest.values());
};

/** Presentation-only adaptation for the existing content cards; never creates or sorts nodes. */
export const creatorTimelineNodeToRound = (
  node: CreatorTimelineContentNode,
  focusId: string | null
): CreatorDeliveryRound | null => {
  if (node.data.contentRoundId === null && !node.data.is_pre_submit)
    return null;
  const statuses: Partial<
    Record<CreatorDeliveryNodeState, CreatorDeliveryRound['status']>
  > = {
    WAITING_BRAND: 'Under_review',
    FEEDBACK_PROVIDED: 'Feedback_provided',
    APPROVED: 'Approved',
    CANCELLED: 'Deal_canceled',
    COMPLETED: 'Submitted'
  };
  const status = statuses[node.state] || '';
  return {
    ...node.data,
    timelineNodeId: node.id,
    timelineState: node.state,
    // Pre-submit drafts have no brand round. Use the API node ID only as a
    // presentation/polling key, never as an editable or submitted round ID.
    contentRoundId: node.data.contentRoundId ?? node.id,
    isCurrent: node.id === focusId,
    status,
    actions: {
      canEdit: !node.data.is_pre_submit && canEditCreatorTimelineNode(node),
      canApprove: false,
      canFeedback: false
    }
  };
};
export const getCreatorTimelineRounds = (
  timeline?: CreatorDeliveryTimeline | null
) =>
  getCreatorTimelineContentNodes(timeline).flatMap((node) => {
    const round = creatorTimelineNodeToRound(
      node,
      timeline?.focus.nodeId ?? null
    );
    return round ? [round] : [];
  });

export const getCreatorTimelineMode = (
  node?: CreatorTimelineContentNode | null
): CreatorPackageDeliveryMode | null =>
  node?.code === 'SCRIPT'
    ? 'script'
    : node?.code === 'DRAFT'
      ? 'content'
      : node?.code === 'FINAL_LINK'
        ? 'finalLink'
        : null;

export const getCreatorSubmitAction = (
  mode: CreatorPackageDeliveryMode
): CreatorDeliveryAction =>
  mode === 'script'
    ? 'SUBMIT_SCRIPT'
    : mode === 'content'
      ? 'SUBMIT_DRAFT'
      : 'SUBMIT_FINAL_LINK';

export const findCreatorDeliveryActionNode = (
  timeline: CreatorDeliveryTimeline | null | undefined,
  mode?: CreatorPackageDeliveryMode | null
) =>
  getCreatorTimelineContentNodes(timeline).find((node) => {
    const nodeMode = getCreatorTimelineMode(node);
    return (
      nodeMode &&
      (!mode || nodeMode === mode) &&
      node.data.target &&
      (node.actions.includes(getCreatorSubmitAction(nodeMode)) ||
        node.actions.includes('EDIT_CONTENT'))
    );
  });

/** Use target order/revisions, not historical channels or the detail's deliveryRevision. */
export const scopeCreatorPackageToTarget = (
  collaborationPackage: CreatorCollaborationPackage,
  target: CreatorDeliveryTarget
): CreatorCollaborationPackage => ({
  ...collaborationPackage,
  placements: target.channels.map((channel) => {
    const placement = collaborationPackage.placements.find(
      (item) => item.channelKey === channel.channelKey
    );
    if (!placement)
      throw new CreatorOrderV2ContractError(
        `Missing details for target channel ${channel.channelKey}. Refresh this order.`
      );
    return {
      ...placement,
      deliveryRevision: channel.expectedDeliveryRevision,
      raw: { ...placement.raw, active: true }
    };
  })
});

export const isCreatorTimelineAccessError = (error: unknown) =>
  error instanceof Error &&
  'code' in error &&
  [40001, 40004, 40011, 47009, 47011, 47012].includes(Number(error.code));
